import React, { useEffect, useRef, useState } from "react";
import "./ImageCarousel.css";

export default function ImageCarousel({
  images = [],
  autoPlay = true,
  interval = 4000,
  showArrows = true,
  showDots = true,
  className = "",
  ArrowLeftIcon,
  ArrowRightIcon,
}) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const hoveredRef = useRef(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const maxZoom = 6;

  const len = images.length;
  const goTo = (i) => setIndex(((i % len) + len) % len);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const circularDelta = (from, to) => {
    if (len === 0) return 0;
    let d = from - to;
    d = ((d + len / 2) % len) - len / 2;
    return d;
  };
  const offsetFor = (d) => (Math.abs(d) === 1 ? d * 70 : d * 140);

  useEffect(() => {
    if (!autoPlay || len <= 1 || lightboxOpen) return;
    timerRef.current = setInterval(() => {
      if (!hoveredRef.current) next();
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [index, autoPlay, interval, len, lightboxOpen]);

  useEffect(() => {
    if (lightboxOpen) setZoom(1);
  }, [index, lightboxOpen]);

  const openLightbox = () => {
    setLightboxOpen(true);
    setZoom(1);
  };
  const closeLightbox = () => setLightboxOpen(false);

  const handleZoomIn = () =>
    setZoom((z) => (z >= maxZoom ? 1 : Math.min(maxZoom, z + 0.25)));
  const handleZoomOut = () => setZoom((z) => Math.max(1, z - 0.25));
  const handleReset = () => setZoom(1);

  return (
    <>
      <div
        className={`carousel-peek ${className}`}
        onMouseEnter={() => (hoveredRef.current = true)}
        onMouseLeave={() => (hoveredRef.current = false)}
      >
        <div className="peek-stage">
          {images.map((img, i) => {
            const d = circularDelta(i, index);
            const pos = d === 0 ? "active" : Math.abs(d) === 1 ? (d < 0 ? "left" : "right") : "far";
            const offsetX = offsetFor(d);

            return (
              <div
                key={i}
                className={`peek-slide ${pos}`}
                style={{ transform: `translateX(${offsetX}%) scale(${pos === "active" ? 1 : 0.88})` }}
                onClick={pos === "active" ? openLightbox : () => goTo(i)}
                role="button"
              >
                <img
                  src={img.src}
                  alt={img.alt || `imagem-${i + 1}`}
                  className="peek-img"
                  draggable="false"
                />
              </div>
            );
          })}
        </div>

        <div className="peek-edge left-edge" />
        <div className="peek-edge right-edge" />

        {showArrows && len > 1 && (
          <>
            <button className="peek-arrow left" onClick={prev} aria-label="Anterior">
              {ArrowLeftIcon ? <ArrowLeftIcon /> : "‹"}
            </button>
            <button className="peek-arrow right" onClick={next} aria-label="Próximo">
              {ArrowRightIcon ? <ArrowRightIcon /> : "›"}
            </button>
          </>
        )}

        {showDots && len > 1 && (
          <div className="peek-dots">
            {images.map((_, i) => (
              <button
                key={i}
                className={`dot ${i === index ? "active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Ir para imagem ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <div className="lb-overlay" onClick={closeLightbox}>
          <div
            className="lb-canvas"
            onClick={(e) => e.stopPropagation()} 
          >
            <img
              src={images[index].src}
              alt={images[index].alt || `imagem-${index + 1}`}
              className="lb-img"
              style={{
                transform: `translate(-50%, -50%) scale(${zoom})`,
                cursor: "default",
              }}
              draggable="false"
            />

            <div className="lb-controls">
              <button onClick={handleZoomIn}>+</button>
              <button onClick={handleZoomOut}>−</button>
              <button onClick={handleReset}>Reset</button>
              <button className="lb-close" onClick={closeLightbox}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
